"use client";

import { medimoQC } from "@medimo/core-api/internal/next";
import type { components } from "@medimo/core-api/internal/openapi";
import { useMemo, useState } from "react";
import { getNotificationPollingIntervalWithJitter } from "../lib/notification-polling";
import { useIsNotificationBannerPollingEnabled } from "./use-notification-banner-polling-enabled";

type UserNotification = components["schemas"]["UserNotificationReadModel"];

/** is_show_in_banner が ON のお知らせのうち、publish_at が最新の1件を返す(無ければ undefined) */
export const selectLatestBannerNotification = (
  notifications: readonly UserNotification[],
): UserNotification | undefined =>
  notifications
    .filter((notification) => notification.is_show_in_banner)
    .reduce<UserNotification | undefined>(
      (latest, current) =>
        !latest || current.publish_at > latest.publish_at ? current : latest,
      undefined,
    );

// バナーに出す対象は種別を絞らず(general・release 両方)、`is_show_in_banner`が
// ONのお知らせのうち最新の1件のみを返す(Bizヒアリングにより確定。DD-2026-0920 セクション8)
export const useActiveBannerNotification = () => {
  // refetchInterval に関数自体を渡すと、react-queryは再描画のたびにそれを呼び直して
  // 次回取得までの待ち時間を測り直す。このコンポーネントは usePathname を使う
  // NotificationTopBanner 配下にあり画面遷移のたびに再描画されるため、関数をそのまま渡すと
  // 遷移が間隔より速い間はタイマーが張り直され続けて取得が一度も走らない恐れがある。
  // マウント時に1回だけ値を決めて固定することでこれを避ける(利用者ごとの値のばらつきは
  // そのまま保たれるため、同時ポーリングの集中を避ける効果は変わらない)
  const [pollingIntervalMs] = useState(
    getNotificationPollingIntervalWithJitter,
  );

  const pollingEnabled = useIsNotificationBannerPollingEnabled();

  const { data } = medimoQC.useQuery(
    "get",
    "/v2/notifications",
    // include_past を明示し、お知らせ一覧(table.tsx)の既定表示(過去のお知らせを含めない)と
    // queryKeyを揃える。これによりキャッシュが共有され、/notifications 画面での重複リクエストを防ぐ
    { params: { query: { include_past: false } } },
    {
      enabled: pollingEnabled,
      refetchInterval: pollingIntervalMs,
      // staleTimeは既定値(providers.tsxの30秒)のままにする。以前ここに0を指定していたが、
      // refetchIntervalはstaleTimeと無関係に動くため2分以内反映には影響しない一方、
      // 0のままだとタブ復帰時(refetchOnWindowFocus)や対象外画面↔対象画面の遷移のたびに
      // (NotificationTopBannerはpathnameで対象判定しておりその都度マウントし直されるため)
      // ポーリング間隔とは無関係に毎回fetchが発生し、試算に含まれない負荷が生じる
      // 既定のリトライ(最大3回)のままだと、バックエンド障害中はポーリング1回が
      // 最大4回のリクエストになる。本機能が最も役立つのはまさにそうした障害の最中であり、
      // そこで負荷を増やすのは本末転倒なため、このバナー自身のポーリングによる取得には
      // リトライさせない(失敗しても次のポーリング間隔で自然に再試行される)。
      // ただしこのクエリはお知らせ一覧(table.tsx)とqueryKeyを共有しており、react-queryは
      // 実際にfetchを発火させたobserver側のoptionsでretryerを作るため、一覧側のobserverが
      // fetchを発火させた場合はこのretry:falseは適用されず、一覧側のデフォルト設定(最大3回)が効く
      retry: false,
    },
  );

  return useMemo(
    () => selectLatestBannerNotification(data?.notifications ?? []),
    [data],
  );
};
